// src/components/RecentlyViewed.js
// Horizontal rail of the shopper's recently viewed products, read from
// localStorage (see lib/recentlyViewed.js). Renders nothing if there's
// no history yet, or once the current product is the only entry.

import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { getScents, NEW_RELEASE_HANDLES } from '../data/scents';
import { GIFT_BAG_HANDLE } from '../context/CartContext';
import { useCurrency } from '../context/CurrencyContext';
import { getRecentHandles } from '../lib/recentlyViewed';
import ProductRatingBadge from './ProductRatingBadge';

const RecentlyViewed = ({ currentHandle }) => {
    const { country, symbol } = useCurrency();
    const [items, setItems] = useState([]);
    const sliderRef = useRef(null);

    useEffect(() => {
        const handles = getRecentHandles(currentHandle);
        if (handles.length === 0) {
            setItems([]);
            return;
        }
        getScents(country)
            .then(all => {
                const byHandle = new Map(all.map(p => [p.handle, p]));
                setItems(handles.map(h => byHandle.get(h)).filter(Boolean));
            })
            .catch(() => setItems([]));
    }, [currentHandle, country]);

    const scrollLeft = () => sliderRef.current?.scrollBy({ left: -280, behavior: 'smooth' });
    const scrollRight = () => sliderRef.current?.scrollBy({ left: 280, behavior: 'smooth' });

    if (items.length === 0) return null;

    return (
        <div className="mb-24">
            <div className="flex items-center justify-between mb-8">
                <h2 className="text-3xl font-light text-foreground tracking-wide">Recently Viewed</h2>
                {items.length > 2 && (
                    <div className="flex gap-2">
                        <button onClick={scrollLeft} className="w-10 h-10 rounded-full border border-border flex items-center justify-center text-muted-foreground hover:text-[#CEAC8F] hover:border-[#CEAC8F] transition-all">
                            <ChevronLeft className="w-5 h-5" />
                        </button>
                        <button onClick={scrollRight} className="w-10 h-10 rounded-full border border-border flex items-center justify-center text-muted-foreground hover:text-[#CEAC8F] hover:border-[#CEAC8F] transition-all">
                            <ChevronRight className="w-5 h-5" />
                        </button>
                    </div>
                )}
            </div>

            <div
                ref={sliderRef}
                className="flex gap-6 overflow-x-auto pb-4"
                style={{ scrollSnapType: 'x mandatory', scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            >
                {items.map((item) => (
                    <Link
                        key={item.id}
                        to={`/product/${item.handle}`}
                        className="flex-shrink-0 w-64 group"
                        style={{ scrollSnapAlign: 'start' }}
                    >
                        <div className="bg-card rounded-2xl overflow-hidden border border-border hover:border-[#CEAC8F]/30 transition-all duration-500">
                            <div className="relative aspect-[3/4] overflow-hidden">
                                {NEW_RELEASE_HANDLES.includes(item.handle) && (
                                    <span className="absolute top-3 left-3 z-10 bg-[#4E2326] text-white text-[10px] font-medium px-2.5 py-1 rounded-full uppercase tracking-widest">
                                        New
                                    </span>
                                )}
                                <img
                                    src={item.image}
                                    alt={item.imageAlt}
                                    loading="lazy"
                                    className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-700"
                                />
                            </div>
                            <div className="p-4">
                                <h3 className="text-foreground font-light group-hover:text-[#CEAC8F] transition-colors mb-1">
                                    {item.name}
                                </h3>
                                <ProductRatingBadge productId={item.id} productTitle={item.name} />
                                {item.handle !== GIFT_BAG_HANDLE && (
                                    <p className="text-muted-foreground/70 text-[10px] tracking-widest mb-1 uppercase">12ml</p>
                                )}
                                <p className="text-[#CEAC8F]">{symbol}{item.price.toFixed(2)}</p>
                            </div>
                        </div>
                    </Link>
                ))}
            </div>
        </div>
    );
};

export default RecentlyViewed;
